import moment from 'moment'

import { convertArrayOfObjectsToCSV, downloadCSV } from '@/util/util'

import { BaseSymconObject, KnockautObject } from '../interfaces'
import { KnockautHandler } from '../knockautHandler'
import { transformIdToNumber } from '../util'

interface AggregationSettings {
  active: boolean
  type: number
  visible: boolean
}

interface SymconAggregationVariable {
  AggregationActive: boolean
  AggregationType: number
  AggregationVisible: boolean
  CounterIgnoreZeros: boolean
  FirstTime: number
  IsValid: boolean
  LastTime: number
  RecordCount: number
  RecordSize: number
  VariableID: number
}

export interface AggregationVariable extends SymconAggregationVariable {
  variable: KnockautObject
  parent: KnockautObject
}

// https://www.symcon.de/service/dokumentation/modulreferenz/archive-control/ac-getaggregatedvalues/
export enum AggregationStep {
  HOURLY = 0,
  DAILY = 1,
  WEEKLY = 2,
  MONTHLY = 3,
  YEARLY = 4,
  FIVE_MINUTE = 5,
  ONE_MINUTE = 6,
}

export interface SymconLoggedValue {
  Duration: number
  TimeStamp: number
  Value: number
}

export interface ArchiveModuleData {
  attributes: {
    Aggregation: string // Stringified JSON
    Statistics: string // Stringified JSON
  }
  configuration: any
  connectionID: number
  lastChange: number
  moduleID: string
  moduleName: string
  moduleType: number
  status: number
}

export interface ArchiveModule extends BaseSymconObject {
  id: number
  data: ArchiveModuleData
}

export class ArchiveHandler {
  private _archive: ArchiveModule
  private _knockautHandler: KnockautHandler
  private _loggedVariables: Map<number, boolean> = new Map()

  constructor(knockautHandler: KnockautHandler, archive: ArchiveModule) {
    this._archive = archive
    this._knockautHandler = knockautHandler
    try {
      this.loadArchive()
    } catch (error) {
      console.warn(error)
    }
  }

  loadArchive() {
    for (const [key, obj] of Object.entries(
      JSON.parse(this._archive.data.attributes.Aggregation)
    )) {
      this._loggedVariables.set(
        transformIdToNumber(key),
        (obj as AggregationSettings).active
      )
    }
  }

  /**
   * Returns true if logging is active for variable
   * @param variableID ID of the variable
   */
  getLoggingStatus(variableID: number): boolean {
    return this._loggedVariables.get(variableID)
  }

  /**
   * Sets logging status for all variables of an instance. Returns true if succesfull, false otherwise
   * @param instanceId ID of instance
   * @param value True if logging should be enabled, false otherwise
   */
  async setLoggingStatusInstance(
    instanceId: number,
    value: boolean
  ): Promise<void> {
    const variables = this._knockautHandler.getVariablesOfInstance(instanceId)
    for (const variable of variables) {
      await this.setLoggingStatus(variable.id, value)
    }
  }

  /**
   * Sets logging status for variable. Returns true if succesfull, false otherwise
   * @param variableID Variable ID
   * @param value True if logging should be enabled, false otherwise
   */
  async setLoggingStatus(variableID: number, value: boolean): Promise<boolean> {
    const response = await this._knockautHandler.parseKnockautResponse(
      'AC_SetLoggingStatus',
      [this._archive.id, variableID, value]
    )
    this._loggedVariables.set(variableID, value)
    return response
  }

  /***
   * https://www.symcon.de/service/dokumentation/modulreferenz/archive-control/ac-getaggregationvariables/
   */
  async getAggregationVariables(): Promise<AggregationVariable[]> {
    const aggrVariables: SymconAggregationVariable[] =
      await this._knockautHandler.parseKnockautResponse(
        'AC_GetAggregationVariables',
        [this._archive.id, true]
      )
    const out: AggregationVariable[] = []
    for (const aggrVariable of aggrVariables) {
      const variable = this._knockautHandler.getObjectById(
        aggrVariable.VariableID
      )
      if (variable) {
        const parent = this._knockautHandler.getObjectById(variable.parentID)
        out.push({
          ...aggrVariable,
          parent,
          variable,
        })
      } else {
        console.warn(`Variable does not exist: ${aggrVariable.VariableID}`)
      }
    }
    return out
  }

  /**
   * https://www.symcon.de/service/dokumentation/modulreferenz/archive-control/ac-getaggregatedvalues/
   * @param variableID      ID of variable
   * @param aggregationStep Aggregation Step
   * @param startTime       Start Time as unix timestamp (0 = from start)
   * @param endTime         End Time as unix timestamp (0 = until now)
   * @param limit           Max number of dataset (0 = No Limit, 10000 max limit always active)
   * @returns
   */
  async getAggregatedValues(
    variableID: string,
    aggregationStep: AggregationStep = AggregationStep.FIVE_MINUTE,
    startTime: number = 0,
    endTime: number = 0,
    limit: number = 0
  ): Promise<any> {
    const values = await this._knockautHandler.parseKnockautResponse(
      'AC_GetAggregatedValues',
      [this._archive.id, variableID, aggregationStep, startTime, endTime, limit]
    )
    return values
  }

  /**
   * https://www.symcon.de/service/dokumentation/modulreferenz/archive-control/ac-getloggedvalues/
   * @param variableID      ID of variable
   * @param startDate       Start Time as unix timestamp (0 = from start)
   * @param endDate         End Time as unix timestamp (0 = until now)
   * @param limit           Max number of dataset (0 = No Limit, 10000 max limit always active)
   * @returns
   */
  async getLoggedValues(
    variableID: string,
    startDate: Date = null,
    endDate: Date = null,
    limit: number = 0
  ): Promise<SymconLoggedValue[]> {
    let startTime,
      endTime = 0
    if (startDate) {
      startTime = Math.round(startDate.getTime() / 1000)
    }
    if (endDate) {
      endTime = Math.round(endDate.getTime() / 1000)
    }
    const response = await this._knockautHandler.parseKnockautResponse(
      'AC_GetLoggedValues',
      [this._archive.id, variableID, startTime, endTime, limit]
    )
    return response
  }

  /**
   * https://www.symcon.de/service/dokumentation/modulreferenz/archive-control/ac-deletevariabledata/
   * @param variableID      ID of variable
   * @param startDate       Start Time as unix timestamp (0 = from start)
   * @param endDate         End Time as unix timestamp (0 = until now)
   * @returns number uf deletes datasets
   */
  async deleteVariableData(
    variableID: string,
    startTime: number,
    endTime: number
  ): Promise<number> {
    const response = await this._knockautHandler.parseKnockautResponse(
      'AC_DeleteVariableData',
      [this._archive.id, variableID, startTime, endTime]
    )
    return response
  }

  async exportLoggedValues(
    values: SymconLoggedValue[],
    range: Date[],
    variableID: string,
    options: {
      type: 'csv'
    }
  ): Promise<void> {
    const filename = `VariableArchiv_${variableID}_${range[0].toISOString()}-${range[1].toISOString()}`
    const csvValues = []
    if (options.type === 'csv') {
      for (const value of values) {
        csvValues.push({
          TimeStamp: moment(value.TimeStamp * 1000).format(
            'DD.MM.YYYY HH:mm:ss'
          ),
          Value: value.Value,
          Duration: value.Duration,
        })
      }
      const csvData = convertArrayOfObjectsToCSV({
        data: csvValues,
      })
      downloadCSV({
        csv: csvData,
        filename: `${filename}.csv`,
      })
    }
  }
}
